import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { finalize, firstValueFrom, forkJoin } from 'rxjs';
import { createApiUrls } from '@wahl-shared/api-urls';
import { environment } from '../environments/environment';
import { AuthSessionService } from './services/auth-session.service';

interface MarketingCollectionResponse<T> {
  data: T[];
}

interface MarketingRecordResponse<T> {
  data: T;
}

interface MarketingContact {
  id: number;
  email: string;
  display_name: string | null;
  consent_status: string;
  created_at: string;
}

interface MarketingContactList {
  id: number;
  name: string;
  description: string | null;
  contact_count: number;
  created_at: string;
}

interface MarketingSenderIdentity {
  id: number;
  email: string;
  name: string | null;
  reply_to: string | null;
  verified: boolean;
  domain: string;
  verification_dns_name: string;
  verification_dns_value: string;
  verification_checked_at: string | null;
  verification_error: string | null;
}

interface MarketingBusinessMailingAddress {
  id: number;
  label: string;
  organization: string;
  street_line1: string;
  street_line2: string | null;
  city: string;
  region: string;
  postal_code: string;
  country: string;
  active: boolean;
}

interface MarketingEmailTemplate {
  id: number;
  name: string;
  subject: string | null;
  body_html: string | null;
  body_text: string | null;
}

interface MarketingCampaign {
  id: number;
  name: string;
  subject: string;
  preview_text: string | null;
  body_html: string | null;
  body_text: string | null;
  status: string;
  contact_list_id: number | null;
  sender_identity_id: number | null;
  business_mailing_address_id: number | null;
  email_template_id: number | null;
}

interface MarketingContactImportInput {
  email: string;
  displayName: string;
  source: string;
  consentStatus: string;
}

interface MailingRequirement {
  label: string;
  detail: string;
  complete: boolean;
}

interface EmailStyle {
  backgroundColor: string;
  cardColor: string;
  accentColor: string;
  buttonColor: string;
  headingColor: string;
  bodyColor: string;
  borderColor: string;
  fontFamily: string;
  cornerRadius: string;
  spacing: 'comfortable' | 'compact' | 'spacious';
}

interface EmailDraft {
  name: string;
  subject: string;
  previewText: string;
  headline: string;
  intro: string;
  details: string[];
  ctaLabel: string;
  ctaUrl: string;
  qrImageUrl: string;
  qrLabel: string;
  hasQrCode: boolean;
  style: EmailStyle;
}

@Component({
  selector: 'app-marketing-email',
  standalone: false,
  templateUrl: './marketing-email.component.html'
})
export class MarketingEmailComponent implements AfterViewInit, OnChanges {
  @Input() visible = false;
  @Input() authToken = '';

  private readonly fb = new FormBuilder();
  private readonly apiUrls = createApiUrls(environment);
  private hasLoadedForToken = '';

  readonly contactForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    displayName: ['']
  });
  readonly contactImportForm = this.fb.nonNullable.group({
    importText: ['']
  });
  readonly listForm = this.fb.nonNullable.group({
    name: ['', [Validators.required]],
    description: [''],
    contactIds: [[] as number[]]
  });
  readonly senderForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    name: [''],
    replyTo: ['']
  });
  readonly addressForm = this.fb.nonNullable.group({
    label: ['HQ', [Validators.required]],
    organization: ['', [Validators.required]],
    streetLine1: ['', [Validators.required]],
    streetLine2: [''],
    city: ['', [Validators.required]],
    region: ['', [Validators.required]],
    postalCode: ['', [Validators.required]],
    country: ['US', [Validators.required]]
  });
  readonly campaignForm = this.fb.nonNullable.group({
    name: ['', [Validators.required]],
    subject: ['', [Validators.required]],
    previewText: [''],
    contactListId: [null as number | null],
    senderIdentityId: [null as number | null],
    businessMailingAddressId: [null as number | null],
    emailTemplateId: [null as number | null]
  });
  contacts: MarketingContact[] = [];
  contactLists: MarketingContactList[] = [];
  senderIdentities: MarketingSenderIdentity[] = [];
  businessAddresses: MarketingBusinessMailingAddress[] = [];
  templates: MarketingEmailTemplate[] = [];
  campaigns: MarketingCampaign[] = [];
  selectedCampaignId: number | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly session: AuthSessionService
  ) {}

  isLoading = false;
  isSavingContact = false;
  isSavingList = false;
  isSavingSender = false;
  isVerifyingSenderId: number | null = null;
  isRotatingSenderVerificationId: number | null = null;
  isSavingAddress = false;
  isSavingTemplate = false;
  isSavingCampaign = false;
  isSendingCampaign = false;
  isImportingContacts = false;
  contactMessage = '';
  contactImportMessage = '';
  listMessage = '';
  senderMessage = '';
  addressMessage = '';
  campaignMessage = '';
  templateDraft: EmailDraft = this.defaultEmailDraft();
  emailDetailsText = this.templateDraft.details.join('\n');

  ngAfterViewInit(): void {
    if (this.visible && this.authToken) {
      void this.activate();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['visible'] || changes['authToken']) && this.visible && this.authToken) {
      void this.activate();
    }

    if (changes['authToken'] && !this.authToken) {
      this.hasLoadedForToken = '';
      this.selectedCampaignId = null;
      this.campaigns = [];
    }
  }

  createContact(): void {
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    const value = this.contactForm.getRawValue();
    this.isSavingContact = true;
    this.contactMessage = '';
    this.http.post<MarketingRecordResponse<MarketingContact>>(this.apiV1Url('marketing/contacts'), {
      contact: {
        email: value.email,
        display_name: value.displayName,
        source: 'ui',
        consent_status: 'opted_in',
        consented_at: new Date().toISOString()
      }
    }, { headers: this.authHeaders() }).subscribe({
      next: (response) => {
        this.contacts = [response.data, ...this.contacts.filter((contact) => contact.id !== response.data.id)];
        this.contactForm.reset({ email: '', displayName: '' });
        this.contactMessage = 'Contact added.';
        this.isSavingContact = false;
      },
      error: (error) => {
        this.contactMessage = this.apiErrorMessage(error, 'Unable to add contact.');
        this.isSavingContact = false;
      }
    });
  }

  async onContactImportFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    try {
      this.contactImportForm.controls.importText.setValue(await file.text());
      this.contactImportMessage = `${file.name} loaded.`;
    } catch {
      this.contactImportMessage = 'Unable to read that file.';
    } finally {
      input.value = '';
    }
  }

  async importContacts(): Promise<void> {
    if (this.isImportingContacts) {
      return;
    }

    const contacts = this.contactImportsFromText(this.contactImportForm.controls.importText.value);
    if (contacts.length === 0) {
      this.contactImportMessage = 'Add contacts as CSV or JSON before importing.';
      return;
    }

    this.isImportingContacts = true;
    this.contactImportMessage = '';

    let createdCount = 0;
    let skippedCount = 0;
    const existingEmails = new Set(this.contacts.map((contact) => contact.email.toLowerCase()));

    for (const contact of contacts) {
      const normalizedEmail = contact.email.toLowerCase();
      if (existingEmails.has(normalizedEmail)) {
        skippedCount += 1;
        continue;
      }

      try {
        const response = await firstValueFrom(this.http.post<MarketingRecordResponse<MarketingContact>>(this.apiV1Url('marketing/contacts'), {
          contact: {
            email: contact.email,
            display_name: contact.displayName,
            source: contact.source,
            consent_status: contact.consentStatus,
            consented_at: new Date().toISOString()
          }
        }, { headers: this.authHeaders() }));
        this.contacts = [response.data, ...this.contacts.filter((existingContact) => existingContact.id !== response.data.id)];
        existingEmails.add(normalizedEmail);
        createdCount += 1;
      } catch {
        skippedCount += 1;
      }
    }

    this.contactImportMessage = `Imported ${createdCount} ${createdCount === 1 ? 'contact' : 'contacts'}. Skipped ${skippedCount}.`;
    this.isImportingContacts = false;
  }

  saveContactList(): void {
    if (this.listForm.invalid) {
      this.listForm.markAllAsTouched();
      return;
    }

    const value = this.listForm.getRawValue();
    this.isSavingList = true;
    this.listMessage = '';
    this.http.post<MarketingRecordResponse<MarketingContactList>>(this.apiV1Url('marketing/contact_lists'), {
      contact_list: {
        name: value.name,
        description: value.description,
        contact_ids: value.contactIds
      }
    }, { headers: this.authHeaders() }).subscribe({
      next: (response) => {
        this.contactLists = [response.data, ...this.contactLists.filter((list) => list.id !== response.data.id)];
        this.listForm.reset({ name: '', description: '', contactIds: [] });
        this.listMessage = 'List saved.';
        this.isSavingList = false;
      },
      error: (error) => {
        this.listMessage = this.apiErrorMessage(error, 'Unable to save list.');
        this.isSavingList = false;
      }
    });
  }

  saveSenderIdentity(): void {
    if (this.senderForm.invalid) {
      this.senderForm.markAllAsTouched();
      return;
    }

    const value = this.senderForm.getRawValue();
    this.isSavingSender = true;
    this.senderMessage = '';
    this.http.post<MarketingRecordResponse<MarketingSenderIdentity>>(this.apiV1Url('marketing/sender_identities'), {
      sender_identity: {
        email: value.email,
        name: value.name,
        reply_to: value.replyTo
      }
    }, { headers: this.authHeaders() }).subscribe({
      next: (response) => {
        this.senderIdentities = [response.data, ...this.senderIdentities.filter((sender) => sender.id !== response.data.id)];
        this.campaignForm.controls.senderIdentityId.setValue(response.data.id);
        this.senderMessage = 'Sender saved. Add the DNS TXT record below, then verify it before sending.';
        this.isSavingSender = false;
      },
      error: (error) => {
        this.senderMessage = this.apiErrorMessage(error, 'Unable to save sender.');
        this.isSavingSender = false;
      }
    });
  }

  verifySenderIdentity(sender: MarketingSenderIdentity): void {
    this.isVerifyingSenderId = sender.id;
    this.senderMessage = '';
    this.http.post<MarketingRecordResponse<MarketingSenderIdentity>>(
      this.apiV1Url(`marketing/sender_identities/${sender.id}/verify`),
      {},
      { headers: this.authHeaders() }
    ).subscribe({
      next: (response) => {
        this.upsertSenderIdentity(response.data);
        this.senderMessage = response.data.verified ? 'Sender domain verified.' : 'Sender domain is not verified yet.';
        this.isVerifyingSenderId = null;
      },
      error: (error) => {
        const responseSender = error.error?.data as MarketingSenderIdentity | undefined;
        if (responseSender) {
          this.upsertSenderIdentity(responseSender);
        }
        this.senderMessage = this.apiErrorMessage(error, 'Unable to verify sender domain.');
        this.isVerifyingSenderId = null;
      }
    });
  }

  rotateSenderVerification(sender: MarketingSenderIdentity): void {
    this.isRotatingSenderVerificationId = sender.id;
    this.senderMessage = '';
    this.http.patch<MarketingRecordResponse<MarketingSenderIdentity>>(
      this.apiV1Url(`marketing/sender_identities/${sender.id}/rotate_verification`),
      {},
      { headers: this.authHeaders() }
    ).subscribe({
      next: (response) => {
        this.upsertSenderIdentity(response.data);
        this.senderMessage = 'Verification token rotated. Update the DNS TXT record before verifying.';
        this.isRotatingSenderVerificationId = null;
      },
      error: (error) => {
        this.senderMessage = this.apiErrorMessage(error, 'Unable to rotate verification token.');
        this.isRotatingSenderVerificationId = null;
      }
    });
  }

  async copySenderDnsName(sender: MarketingSenderIdentity): Promise<void> {
    await this.copyText(
      sender.verification_dns_name,
      `TXT host/name copied for ${sender.domain}.`,
      'Unable to copy TXT host/name.'
    );
  }

  async copySenderDnsValue(sender: MarketingSenderIdentity): Promise<void> {
    await this.copyText(
      sender.verification_dns_value,
      `TXT value/token copied for ${sender.domain}.`,
      'Unable to copy TXT value/token.'
    );
  }

  saveBusinessAddress(): void {
    if (this.addressForm.invalid) {
      this.addressForm.markAllAsTouched();
      return;
    }

    const value = this.addressForm.getRawValue();
    this.isSavingAddress = true;
    this.addressMessage = '';
    this.http.post<MarketingRecordResponse<MarketingBusinessMailingAddress>>(this.apiV1Url('marketing/business_mailing_addresses'), {
      business_mailing_address: {
        label: value.label,
        organization: value.organization,
        street_line1: value.streetLine1,
        street_line2: value.streetLine2,
        city: value.city,
        region: value.region,
        postal_code: value.postalCode,
        country: value.country,
        active: true
      }
    }, { headers: this.authHeaders() }).subscribe({
      next: (response) => {
        this.businessAddresses = [response.data, ...this.businessAddresses.filter((address) => address.id !== response.data.id)];
        this.campaignForm.controls.businessMailingAddressId.setValue(response.data.id);
        this.addressMessage = 'Mailing address saved.';
        this.isSavingAddress = false;
      },
      error: (error) => {
        this.addressMessage = this.apiErrorMessage(error, 'Unable to save mailing address.');
        this.isSavingAddress = false;
      }
    });
  }

  saveTemplate(): void {
    const name = this.campaignForm.controls.name.value.trim();
    const subject = this.campaignForm.controls.subject.value.trim();
    if (!name) {
      this.campaignMessage = 'Add a campaign name before saving a layout.';
      return;
    }

    const content = this.templateContent();
    this.isSavingTemplate = true;
    this.campaignMessage = '';
    this.http.post<MarketingRecordResponse<MarketingEmailTemplate>>(this.apiV1Url('marketing/email_templates'), {
      email_template: {
        name,
        subject,
        body_html: content.html,
        body_text: content.text
      }
    }, { headers: this.authHeaders() }).subscribe({
      next: (response) => {
        this.templates = [response.data, ...this.templates.filter((template) => template.id !== response.data.id)];
        this.campaignForm.controls.emailTemplateId.setValue(response.data.id);
        this.campaignMessage = 'Layout saved.';
        this.isSavingTemplate = false;
      },
      error: (error) => {
        this.campaignMessage = this.apiErrorMessage(error, 'Unable to save layout.');
        this.isSavingTemplate = false;
      }
    });
  }

  saveCampaign(): void {
    if (this.campaignForm.invalid) {
      this.campaignForm.markAllAsTouched();
      return;
    }

    const value = this.campaignForm.getRawValue();
    const content = this.templateContent();
    const payload = {
      campaign: {
        name: value.name,
        subject: value.subject,
        preview_text: value.previewText,
        contact_list_id: value.contactListId,
        sender_identity_id: value.senderIdentityId,
        business_mailing_address_id: value.businessMailingAddressId,
        email_template_id: value.emailTemplateId,
        body_html: content.html,
        body_text: content.text
      }
    };
    const request = this.selectedCampaignId
      ? this.http.patch<MarketingRecordResponse<MarketingCampaign>>(this.apiV1Url(`marketing/campaigns/${this.selectedCampaignId}`), payload, { headers: this.authHeaders() })
      : this.http.post<MarketingRecordResponse<MarketingCampaign>>(this.apiV1Url('marketing/campaigns'), payload, { headers: this.authHeaders() });

    this.isSavingCampaign = true;
    this.campaignMessage = '';
    request.subscribe({
      next: (response) => {
        this.selectedCampaignId = response.data.id;
        this.upsertCampaign(response.data);
        this.campaignMessage = 'Campaign saved.';
        this.isSavingCampaign = false;
      },
      error: (error) => {
        this.campaignMessage = this.apiErrorMessage(error, 'Unable to save campaign.');
        this.isSavingCampaign = false;
      }
    });
  }

  sendCampaign(): void {
    if (!this.selectedCampaignId) {
      return;
    }

    this.isSendingCampaign = true;
    this.campaignMessage = '';
    this.http.post<MarketingRecordResponse<MarketingCampaign>>(
      this.apiV1Url(`marketing/campaigns/${this.selectedCampaignId}/send`),
      {},
      { headers: this.authHeaders() }
    ).subscribe({
      next: (response) => {
        this.upsertCampaign(response.data);
        this.campaignMessage = 'Campaign queued for sending.';
        this.isSendingCampaign = false;
      },
      error: (error) => {
        this.campaignMessage = this.apiErrorMessage(error, 'Unable to queue campaign.');
        this.isSendingCampaign = false;
      }
    });
  }

  selectCampaignId(value: number | string | null | undefined): void {
    const id = Number(value);
    const campaign = this.campaigns.find((candidate) => candidate.id === id);
    if (!campaign) {
      return;
    }

    this.selectedCampaignId = campaign.id;
    this.campaignForm.reset({
      name: campaign.name,
      subject: campaign.subject,
      previewText: campaign.preview_text || '',
      contactListId: campaign.contact_list_id,
      senderIdentityId: campaign.sender_identity_id,
      businessMailingAddressId: campaign.business_mailing_address_id,
      emailTemplateId: campaign.email_template_id
    });
    this.setTemplateMarkup(campaign.body_html || this.defaultMarkup());
    this.campaignMessage = '';
  }

  selectTemplateId(value: number | string | null | undefined): void {
    const id = Number(value);
    const template = this.templates.find((candidate) => candidate.id === id);
    if (!template) {
      return;
    }

    this.campaignForm.patchValue({
      subject: this.campaignForm.controls.subject.value || template.subject || ''
    });
    this.setTemplateMarkup(template.body_html || this.defaultMarkup());
  }

  newCampaign(): void {
    this.selectedCampaignId = null;
    this.campaignForm.reset({
      name: '',
      subject: '',
      previewText: '',
      contactListId: null,
      senderIdentityId: this.senderIdentities[0]?.id || null,
      businessMailingAddressId: this.businessAddresses[0]?.id || null,
      emailTemplateId: null
    });
    this.setTemplateDraft(this.defaultEmailDraft());
    this.campaignMessage = '';
  }

  mailingRequirements(): MailingRequirement[] {
    const sender = this.selectedSenderIdentity();
    const address = this.selectedBusinessAddress();
    const hasSubject = this.campaignForm.controls.subject.value.trim().length > 0;
    const hasBody = this.templateDraft.headline.trim().length > 0 && this.templateDraft.intro.trim().length > 0;
    const hasRecipients = this.hasDeliverableRecipients();

    return [
      {
        label: 'Forgery protection: verified sender domain',
        detail: sender
          ? sender.verified
            ? `${sender.domain} has the required domain-ownership TXT proof for ${sender.email}.`
            : `The API blocks sending until DNS publishes TXT ${sender.verification_dns_name} with value ${sender.verification_dns_value}.`
          : 'Create a sender identity using an email address at the domain you want to send from.',
        complete: Boolean(sender?.verified)
      },
      {
        label: 'Physical mailing address',
        detail: address
          ? `${address.organization}, ${address.street_line1}, ${address.city}, ${address.region} ${address.postal_code}`
          : 'Save an active business mailing address.',
        complete: Boolean(address)
      },
      {
        label: 'Unsubscribe link host',
        detail: 'The app host provides unsubscribe links for queued marketing email.',
        complete: true
      },
      {
        label: 'Subject and body',
        detail: hasSubject && hasBody ? 'Campaign subject and body are present.' : 'Add a subject, headline, and intro before sending.',
        complete: hasSubject && hasBody
      },
      {
        label: 'Deliverable recipients',
        detail: hasRecipients ? 'At least one opted-in or implied-consent contact is available.' : 'Add or import at least one opted-in contact.',
        complete: hasRecipients
      }
    ];
  }

  private async activate(): Promise<void> {
    if (this.hasLoadedForToken !== this.authToken) {
      this.loadMarketingData();
    }
  }

  private loadMarketingData(): void {
    this.isLoading = true;
    forkJoin({
      contacts: this.http.get<MarketingCollectionResponse<MarketingContact>>(this.apiV1Url('marketing/contacts'), { headers: this.authHeaders() }),
      contactLists: this.http.get<MarketingCollectionResponse<MarketingContactList>>(this.apiV1Url('marketing/contact_lists'), { headers: this.authHeaders() }),
      senderIdentities: this.http.get<MarketingCollectionResponse<MarketingSenderIdentity>>(this.apiV1Url('marketing/sender_identities'), { headers: this.authHeaders() }),
      businessAddresses: this.http.get<MarketingCollectionResponse<MarketingBusinessMailingAddress>>(this.apiV1Url('marketing/business_mailing_addresses'), { headers: this.authHeaders() }),
      templates: this.http.get<MarketingCollectionResponse<MarketingEmailTemplate>>(this.apiV1Url('marketing/email_templates'), { headers: this.authHeaders() }),
      campaigns: this.http.get<MarketingCollectionResponse<MarketingCampaign>>(this.apiV1Url('marketing/campaigns'), { headers: this.authHeaders() })
    }).pipe(
      finalize(() => {
        this.isLoading = false;
      })
    ).subscribe({
      next: (response) => {
        this.contacts = response.contacts.data;
        this.contactLists = response.contactLists.data;
        this.senderIdentities = response.senderIdentities.data;
        this.businessAddresses = response.businessAddresses.data;
        this.templates = response.templates.data;
        this.campaigns = response.campaigns.data;
        this.hasLoadedForToken = this.authToken;
        this.applyDefaultCampaignAssociations();
      },
      error: (error) => {
        this.campaignMessage = this.apiErrorMessage(error, 'Unable to load marketing email data.');
      }
    });
  }

  private applyDefaultCampaignAssociations(): void {
    if (!this.campaignForm.controls.senderIdentityId.value && this.senderIdentities[0]) {
      this.campaignForm.controls.senderIdentityId.setValue(this.senderIdentities[0].id);
    }

    if (!this.campaignForm.controls.businessMailingAddressId.value && this.businessAddresses[0]) {
      this.campaignForm.controls.businessMailingAddressId.setValue(this.businessAddresses[0].id);
    }

    if (!this.campaignForm.controls.contactListId.value && this.contactLists[0]) {
      this.campaignForm.controls.contactListId.setValue(this.contactLists[0].id);
    }
  }

  private upsertCampaign(record: MarketingCampaign): void {
    this.campaigns = [record, ...this.campaigns.filter((campaign) => campaign.id !== record.id)];
  }

  private upsertSenderIdentity(record: MarketingSenderIdentity): void {
    this.senderIdentities = [record, ...this.senderIdentities.filter((sender) => sender.id !== record.id)];
  }

  private selectedSenderIdentity(): MarketingSenderIdentity | null {
    const selectedId = this.campaignForm.controls.senderIdentityId.value;
    return this.senderIdentities.find((sender) => sender.id === selectedId) || this.senderIdentities[0] || null;
  }

  private selectedBusinessAddress(): MarketingBusinessMailingAddress | null {
    const selectedId = this.campaignForm.controls.businessMailingAddressId.value;
    const selectedAddress = this.businessAddresses.find((address) => address.id === selectedId);
    return selectedAddress?.active ? selectedAddress : this.businessAddresses.find((address) => address.active) || null;
  }

  private hasDeliverableRecipients(): boolean {
    const selectedListId = this.campaignForm.controls.contactListId.value;
    const selectedList = this.contactLists.find((list) => list.id === selectedListId);
    if (selectedList) {
      return selectedList.contact_count > 0;
    }

    return this.contacts.some((contact) => contact.consent_status === 'opted_in' || contact.consent_status === 'implied');
  }

  private async copyText(value: string, successMessage: string, failureMessage: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      this.senderMessage = successMessage;
    } catch {
      this.senderMessage = failureMessage;
    }
  }

  setEmailDetailsText(value: string): void {
    this.emailDetailsText = value;
    this.templateDraft = {
      ...this.templateDraft,
      details: value
        .split(/\r?\n/)
        .map((detail) => detail.trim())
        .filter(Boolean)
        .slice(0, 6)
    };
    this.campaignMessage = '';
  }

  onTemplateChanged(): void {
    this.campaignMessage = '';
  }

  setTemplateStyleValue(field: keyof EmailStyle, value: string): void {
    this.templateDraft = {
      ...this.templateDraft,
      style: this.normalizeEmailStyle({
        ...this.templateDraft.style,
        [field]: value
      })
    };
    this.campaignMessage = '';
  }

  emailPreviewStyle(): Record<string, string> {
    const style = this.normalizeEmailStyle(this.templateDraft.style);

    return {
      '--email-background': style.backgroundColor,
      '--email-card': style.cardColor,
      '--email-accent': style.accentColor,
      '--email-button': style.buttonColor,
      '--email-heading': style.headingColor,
      '--email-body': style.bodyColor,
      '--email-border': style.borderColor,
      '--email-font': style.fontFamily,
      '--email-radius': style.cornerRadius,
      '--email-hero-padding': this.heroPadding(style.spacing),
      '--email-section-padding': this.sectionPadding(style.spacing)
    };
  }

  private setTemplateDraft(draft: Partial<EmailDraft>): void {
    this.templateDraft = this.normalizeEmailDraft(draft);
    this.emailDetailsText = this.templateDraft.details.join('\n');
    this.campaignMessage = '';
  }

  private normalizeEmailDraft(draft: Partial<EmailDraft>): EmailDraft {
    return {
      name: draft.name || 'Email campaign',
      subject: draft.subject || 'Your offer is ready',
      previewText: draft.previewText || '',
      headline: draft.headline || 'Your next offer is ready',
      intro: draft.intro || 'Use this message to introduce the offer and invite customers to take action.',
      details: draft.details?.length ? draft.details : ['Easy to scan', 'Fast to open', 'Ready for customers'],
      ctaLabel: draft.ctaLabel || 'Open offer',
      ctaUrl: draft.ctaUrl || 'https://example.com',
      qrImageUrl: draft.qrImageUrl || '',
      qrLabel: draft.qrLabel || 'Scan the QR code',
      hasQrCode: Boolean(draft.hasQrCode || draft.qrImageUrl),
      style: this.normalizeEmailStyle(draft.style)
    };
  }

  private emailMarkupFromDraft(draft: Partial<EmailDraft>): string {
    const normalizedDraft = this.normalizeEmailDraft(draft);
    const detailItems = normalizedDraft.details.map((detail) => `<li>${this.escapeHtml(detail)}</li>`).join('');

    return `
      <style>${this.defaultStyles(normalizedDraft.style)}</style>
      <table class="email-wrap" width="100%" cellpadding="0" cellspacing="0">
        <tr>
          <td align="center">
            <table class="email-card" width="640" cellpadding="0" cellspacing="0">
              <tr>
                <td class="email-hero">
                  <p class="eyebrow">Wahl Advertising</p>
                  <h1>${this.escapeHtml(normalizedDraft.headline)}</h1>
                  <p>${this.escapeHtml(normalizedDraft.intro)}</p>
                  <a class="button" href="${this.escapeAttribute(normalizedDraft.ctaUrl)}">${this.escapeHtml(normalizedDraft.ctaLabel)}</a>
                </td>
              </tr>
              <tr>
                <td class="email-section">
                  <h2>Highlights</h2>
                  <ul>${detailItems}</ul>
                </td>
              </tr>
              ${this.qrBlockMarkup(normalizedDraft)}
            </table>
          </td>
        </tr>
      </table>
    `;
  }

  private qrBlockMarkup(draft: Partial<EmailDraft>): string {
    if (!draft.hasQrCode) {
      return '';
    }

    const ctaUrl = draft.ctaUrl || 'https://example.com';
    const qrImageUrl = draft.qrImageUrl || '';
    const qrLabel = draft.qrLabel || 'Scan the QR code';

    return `
      <tr>
        <td class="email-qr">
          ${qrImageUrl ? `<a href="${this.escapeAttribute(ctaUrl)}"><img src="${this.escapeAttribute(qrImageUrl)}" width="156" height="156" alt="${this.escapeAttribute(qrLabel)}"></a>` : ''}
          <h2>${this.escapeHtml(qrLabel)}</h2>
          <p>Use the QR code or button to open the linked page.</p>
          <a class="text-link" href="${this.escapeAttribute(ctaUrl)}">${this.escapeHtml(ctaUrl)}</a>
        </td>
      </tr>
    `;
  }

  private contactImportsFromText(value: string): MarketingContactImportInput[] {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return [];
    }

    const records = trimmedValue.startsWith('{') || trimmedValue.startsWith('[')
      ? this.contactImportsFromJson(trimmedValue)
      : this.contactImportsFromCsv(trimmedValue);
    const seenEmails = new Set<string>();

    return records.filter((record) => {
      const email = record.email.toLowerCase();
      if (!this.isValidEmail(email) || seenEmails.has(email)) {
        return false;
      }

      seenEmails.add(email);
      return true;
    });
  }

  private contactImportsFromJson(value: string): MarketingContactImportInput[] {
    let parsed: unknown;

    try {
      parsed = JSON.parse(value);
    } catch {
      return [];
    }

    const records = Array.isArray(parsed)
      ? parsed
      : this.objectArrayProperty(parsed, 'contacts');

    return records.map((record) => this.contactImportFromUnknown(record)).filter((record): record is MarketingContactImportInput => Boolean(record));
  }

  private objectArrayProperty(value: unknown, property: string): unknown[] {
    if (!value || typeof value !== 'object' || !(property in value)) {
      return [];
    }

    const candidate = (value as Record<string, unknown>)[property];
    return Array.isArray(candidate) ? candidate : [];
  }

  private contactImportFromUnknown(value: unknown): MarketingContactImportInput | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const record = value as Record<string, unknown>;
    const email = this.stringValue(record['email']);
    if (!email) {
      return null;
    }

    return {
      email,
      displayName: this.stringValue(record['display_name']) || this.stringValue(record['displayName']) || this.stringValue(record['name']),
      source: this.stringValue(record['source']) || 'import',
      consentStatus: this.stringValue(record['consent_status']) || this.stringValue(record['consentStatus']) || 'opted_in'
    };
  }

  private contactImportsFromCsv(value: string): MarketingContactImportInput[] {
    const rows = this.parseCsvRows(value).filter((row) => row.some((cell) => cell.trim()));
    if (rows.length === 0) {
      return [];
    }

    const header = rows[0].map((cell) => this.csvHeaderKey(cell));
    const hasHeader = header.includes('email');
    const dataRows = hasHeader ? rows.slice(1) : rows;
    const emailIndex = hasHeader ? header.indexOf('email') : 0;
    const displayNameIndex = hasHeader ? this.firstHeaderIndex(header, ['display_name', 'displayname', 'name', 'full_name']) : 1;
    const sourceIndex = hasHeader ? header.indexOf('source') : -1;
    const consentStatusIndex = hasHeader ? this.firstHeaderIndex(header, ['consent_status', 'consentstatus']) : -1;

    return dataRows
      .map((row) => ({
        email: (row[emailIndex] || '').trim(),
        displayName: displayNameIndex >= 0 ? (row[displayNameIndex] || '').trim() : '',
        source: sourceIndex >= 0 ? (row[sourceIndex] || '').trim() || 'import' : 'import',
        consentStatus: consentStatusIndex >= 0 ? (row[consentStatusIndex] || '').trim() || 'opted_in' : 'opted_in'
      }))
      .filter((record) => Boolean(record.email));
  }

  private parseCsvRows(value: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let field = '';
    let inQuotes = false;

    for (let index = 0; index < value.length; index += 1) {
      const char = value[index];
      const nextChar = value[index + 1];

      if (char === '"' && inQuotes && nextChar === '"') {
        field += '"';
        index += 1;
      } else if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        row.push(field);
        field = '';
      } else if ((char === '\n' || char === '\r') && !inQuotes) {
        row.push(field);
        rows.push(row);
        row = [];
        field = '';
        if (char === '\r' && nextChar === '\n') {
          index += 1;
        }
      } else {
        field += char;
      }
    }

    row.push(field);
    rows.push(row);
    return rows;
  }

  private csvHeaderKey(value: string): string {
    return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  private firstHeaderIndex(header: string[], candidates: string[]): number {
    return candidates.map((candidate) => header.indexOf(candidate)).find((index) => index >= 0) ?? -1;
  }

  private stringValue(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private isValidEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  private templateContent(): { html: string; text: string } {
    const html = this.emailMarkupFromDraft(this.templateDraft);
    const text = this.plainTextFromHtml(html);

    return { html, text };
  }

  private setTemplateMarkup(html: string): void {
    this.setTemplateDraft(this.emailDraftFromMarkup(html));
  }

  private plainTextFromHtml(html: string): string {
    const element = document.createElement('div');
    element.innerHTML = html;
    return (element.textContent || '').replace(/\s+/g, ' ').trim();
  }

  private authHeaders(): Record<string, string> {
    return this.session.authHeaders();
  }

  private apiV1Url(path: string): string {
    return this.apiUrls.v1(path);
  }

  private apiErrorMessage(error: unknown, fallback: string): string {
    if (!error || typeof error !== 'object' || !('error' in error)) {
      return fallback;
    }

    const responseError = (error as { error?: unknown }).error;
    if (responseError && typeof responseError === 'object') {
      const messages = responseError as { errors?: string[]; error?: string };
      return messages.errors?.join(', ') || messages.error || fallback;
    }

    return fallback;
  }

  private defaultEmailStyle(): EmailStyle {
    return {
      backgroundColor: '#f3f6f8',
      cardColor: '#ffffff',
      accentColor: '#164c63',
      buttonColor: '#164c63',
      headingColor: '#111827',
      bodyColor: '#374151',
      borderColor: '#d9dee5',
      fontFamily: 'Arial, sans-serif',
      cornerRadius: '0px',
      spacing: 'comfortable'
    };
  }

  private normalizeEmailStyle(style: Partial<EmailStyle> | undefined): EmailStyle {
    const fallback = this.defaultEmailStyle();
    const spacing = style?.spacing === 'compact' || style?.spacing === 'spacious' ? style.spacing : fallback.spacing;

    return {
      backgroundColor: this.safeEmailColor(style?.backgroundColor, fallback.backgroundColor),
      cardColor: this.safeEmailColor(style?.cardColor, fallback.cardColor),
      accentColor: this.safeEmailColor(style?.accentColor, fallback.accentColor),
      buttonColor: this.safeEmailColor(style?.buttonColor, style?.accentColor || fallback.buttonColor),
      headingColor: this.safeEmailColor(style?.headingColor, fallback.headingColor),
      bodyColor: this.safeEmailColor(style?.bodyColor, fallback.bodyColor),
      borderColor: this.safeEmailColor(style?.borderColor, fallback.borderColor),
      fontFamily: this.safeEmailFontFamily(style?.fontFamily),
      cornerRadius: this.safeEmailRadius(style?.cornerRadius, fallback.cornerRadius),
      spacing
    };
  }

  private emailStyleFromMarkup(documentFragment: Document): Partial<EmailStyle> {
    const styles = Array.from(documentFragment.querySelectorAll('style')).map((element) => element.textContent || '').join(' ');

    return {
      backgroundColor: this.cssPropertyFromText(styles, 'email-wrap', 'background'),
      cardColor: this.cssPropertyFromText(styles, 'email-card', 'background'),
      accentColor: this.cssPropertyFromText(styles, 'eyebrow', 'color'),
      buttonColor: this.cssPropertyFromText(styles, 'button', 'background'),
      headingColor: this.cssPropertyFromText(styles, 'email-hero', 'color'),
      bodyColor: this.cssPropertyFromText(styles, 'email-hero p', 'color'),
      borderColor: this.cssBorderColorFromText(styles),
      fontFamily: this.cssPropertyFromText(styles, 'email-wrap', 'font-family'),
      cornerRadius: this.cssPropertyFromText(styles, 'email-card', 'border-radius')
    };
  }

  private cssPropertyFromText(styles: string, selector: string, property: string): string {
    const escapedSelector = selector.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = styles.match(new RegExp(`\\.${escapedSelector}\\s*\\{[^}]*\\b${property}\\s*:\\s*([^;]+)`, 'i'));
    return match?.[1]?.trim() || '';
  }

  private cssBorderColorFromText(styles: string): string {
    return styles.match(/\.email-card\s*\{[^}]*border\s*:\s*[^;]*\s(#[0-9a-f]{3,6})/i)?.[1] || '';
  }

  private safeEmailColor(value: string | undefined, fallback: string): string {
    const color = (value || '').trim();
    return /^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(color) ? color : fallback;
  }

  private safeEmailFontFamily(value: string | undefined): string {
    const allowedFonts = ['Arial, sans-serif', 'Helvetica, Arial, sans-serif', 'Georgia, serif', 'Verdana, sans-serif'];
    return allowedFonts.find((font) => font === value) || 'Arial, sans-serif';
  }

  private safeEmailRadius(value: string | undefined, fallback: string): string {
    const radius = (value || '').trim();
    return /^(0|4|8|12)px$/.test(radius) ? radius : fallback;
  }

  private heroPadding(spacing: EmailStyle['spacing']): string {
    if (spacing === 'compact') {
      return '24px';
    }

    if (spacing === 'spacious') {
      return '40px';
    }

    return '32px';
  }

  private sectionPadding(spacing: EmailStyle['spacing']): string {
    if (spacing === 'compact') {
      return '20px 24px 24px';
    }

    if (spacing === 'spacious') {
      return '32px 40px 40px';
    }

    return '24px 32px 32px';
  }

  private escapeHtml(value: string): string {
    const element = document.createElement('span');
    element.textContent = value;
    return element.innerHTML;
  }

  private escapeAttribute(value: string): string {
    return this.escapeHtml(value).replace(/"/g, '&quot;');
  }

  private defaultEmailDraft(): EmailDraft {
    return {
      name: 'Email campaign',
      subject: 'Your offer is ready',
      previewText: '',
      headline: 'Campaign headline',
      intro: 'Use this message to build a promotional email, product announcement, coupon, or customer follow-up.',
      details: ['Add the offer details, event timing, or business update here'],
      ctaLabel: 'Open offer',
      ctaUrl: 'https://example.com',
      qrImageUrl: '',
      qrLabel: 'Scan the QR code',
      hasQrCode: false,
      style: this.defaultEmailStyle()
    };
  }

  private emailDraftFromMarkup(html: string): Partial<EmailDraft> {
    const documentFragment = document.implementation.createHTMLDocument('');
    documentFragment.documentElement.innerHTML = html;
    const details = Array.from(documentFragment.querySelectorAll('li'))
      .map((element) => (element.textContent || '').replace(/\s+/g, ' ').trim())
      .filter(Boolean);
    const cta = documentFragment.querySelector('.button, a[href]') as HTMLAnchorElement | null;
    const qrImage = documentFragment.querySelector('.email-qr img') as HTMLImageElement | null;
    const qrHeading = documentFragment.querySelector('.email-qr h2');

    return {
      headline: documentFragment.querySelector('h1')?.textContent?.trim() || undefined,
      intro: documentFragment.querySelector('.email-hero p:not(.eyebrow)')?.textContent?.trim()
        || documentFragment.querySelector('p')?.textContent?.trim()
        || undefined,
      details,
      ctaLabel: cta?.textContent?.trim() || undefined,
      ctaUrl: cta?.getAttribute('href') || undefined,
      qrImageUrl: qrImage?.getAttribute('src') || undefined,
      qrLabel: qrHeading?.textContent?.trim() || undefined,
      hasQrCode: Boolean(qrImage || documentFragment.querySelector('.email-qr')),
      style: this.normalizeEmailStyle(this.emailStyleFromMarkup(documentFragment))
    };
  }

  private defaultMarkup(): string {
    return this.emailMarkupFromDraft(this.defaultEmailDraft());
  }

  private defaultStyles(styleInput: Partial<EmailStyle> = {}): string {
    const style = this.normalizeEmailStyle(styleInput);

    return `
      .email-wrap { font-family: ${style.fontFamily}; background: ${style.backgroundColor}; padding: 24px; }
      .email-card { background: ${style.cardColor}; border: 1px solid ${style.borderColor}; border-radius: ${style.cornerRadius}; overflow: hidden; }
      .email-hero { padding: ${this.heroPadding(style.spacing)}; color: ${style.headingColor}; }
      .email-hero h1 { margin: 0 0 12px; font-size: 32px; line-height: 1.15; }
      .email-hero p { margin: 0 0 20px; color: ${style.bodyColor}; line-height: 1.5; }
      .eyebrow { font-size: 12px; font-weight: 700; letter-spacing: 0; text-transform: uppercase; color: ${style.accentColor}; }
      .button { display: inline-block; padding: 12px 18px; background: ${style.buttonColor}; color: #ffffff; text-decoration: none; border-radius: ${style.cornerRadius}; }
      .email-section { padding: ${this.sectionPadding(style.spacing)}; color: ${style.headingColor}; border-top: 1px solid ${style.borderColor}; }
      .email-section h2 { margin: 0 0 10px; font-size: 20px; }
      .email-section p { margin: 0; color: ${style.bodyColor}; line-height: 1.5; }
      .email-section ul { margin: 0; padding-left: 20px; color: ${style.bodyColor}; line-height: 1.5; }
      .email-section li { margin: 0 0 8px; }
      .email-qr { padding: ${this.sectionPadding(style.spacing)}; color: ${style.headingColor}; border-top: 1px solid ${style.borderColor}; text-align: center; }
      .email-qr p, .text-link { color: ${style.bodyColor}; line-height: 1.5; }
    `;
  }
}
